import BackButton from '../components/BackButton';
import { useState, useEffect } from 'react';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

const ROLE_LABELS = {
  superadmin: '👑 Суперадмин',
  admin: '🔧 Админ',
  manager: '👔 Менеджер',
  client: '👤 Клиент',
};

const ROLE_COLORS = {
  superadmin: '#f59e0b',
  admin: '#8b5cf6',
  manager: '#10b981',
  client: '#6b7280',
};

export default function AdminHierarchy() {
  const [loading, setLoading] = useState(true);
  const { t } = useI18n();
  const [hierarchy, setHierarchy] = useState(null);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [action, setAction] = useState(null); // 'role' | 'manager'
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [hierRes, usersRes] = await Promise.all([
        api.get('/admin/users/hierarchy'),
        api.get('/admin/users?limit=500'),
      ]);
      setHierarchy(hierRes.data);
      setUsers(usersRes.data.users || []);
    } catch (e) {
      setError(e.userMessage || 'Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  const setRole = async (userId, role) => {
    setSaving(true);
    try {
      await api.post(`/admin/users/${userId}/set-role`, { role });
      await loadData();
      setSelectedUser(null);
      setAction(null);
    } catch (e) {
      alert('Ошибка: ' + (e.userMessage || e.message));
    } finally {
      setSaving(false);
    }
  };

  const assignManager = async (userId, managerId) => {
    setSaving(true);
    try {
      await api.post(`/admin/users/${userId}/assign-manager`, { manager_id: managerId || null });
      await loadData();
      setSelectedUser(null);
      setAction(null);
    } catch (e) {
      alert('Ошибка: ' + (e.userMessage || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="page"><BackButton /><div className="loader">{t('common.loading')}</div></div>;

  const managers = users.filter(u => ['superadmin', 'admin', 'manager'].includes(u.role));

  return (
    <div className="page"><BackButton />
      <h1>👥 Иерархия пользователей</h1>
      
      {error && <div className="error-box">{error}</div>}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8, marginBottom: 24 }}>
        {['superadmins', 'admins', 'managers', 'clients'].map(key => (
          <div key={key} style={{ 
            background: 'rgba(255,255,255,0.05)', 
            padding: 12, 
            borderRadius: 8, 
            textAlign: 'center' 
          }}>
            <div style={{ fontSize: 24, fontWeight: 'bold' }}>{hierarchy?.[key]?.length || 0}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>{key}</div>
          </div>
        ))}
      </div>

      {/* User List with Hierarchy */}
      <div className="list">
        {users.map(u => {
          const managerName = u.manager_id 
            ? users.find(m => m.id === u.manager_id)?.first_name || `ID:${u.manager_id}`
            : null;
          
          return (
            <div 
              key={u.id} 
              className="list-item"
              style={{ cursor: 'pointer' }}
              onClick={() => { setSelectedUser(u); setAction(null); }}
            >
              <div className="list-main">
                <div className="list-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ 
                    background: ROLE_COLORS[u.role], 
                    padding: '2px 8px', 
                    borderRadius: 4, 
                    fontSize: 11 
                  }}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                  {u.first_name} {u.last_name}
                </div>
                <div className="list-sub">
                  @{u.telegram_username || u.telegram_id}
                  {managerName && <span style={{ marginLeft: 8, color: '#10b981' }}>← {managerName}</span>}
                </div>
              </div>
              <div style={{ fontSize: 20 }}>→</div>
            </div>
          );
        })}
      </div>

      {/* User Modal */}
      {selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: 16,
        }}>
          <div style={{
            background: '#1a1a2e',
            borderRadius: 12,
            padding: 24,
            maxWidth: 400,
            width: '100%',
          }}>
            <h2 style={{ marginTop: 0 }}>
              {selectedUser.first_name} {selectedUser.last_name}
            </h2>
            <p style={{ opacity: 0.7 }}>@{selectedUser.telegram_username || selectedUser.telegram_id}</p>
            
            <div style={{ 
              background: ROLE_COLORS[selectedUser.role], 
              padding: '8px 16px', 
              borderRadius: 8, 
              display: 'inline-block',
              marginBottom: 16 
            }}>
              {ROLE_LABELS[selectedUser.role]}
            </div>

            {!action && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <button className="btn btn-primary" onClick={() => setAction('role')}>
                  🎭 Изменить роль
                </button>
                <button className="btn btn-secondary" onClick={() => setAction('manager')}>
                  👔 Назначить менеджера
                </button>
                <button 
                  className="btn btn-secondary" 
                  style={{ marginTop: 16 }}
                  onClick={() => { setSelectedUser(null); setAction(null); }}
                >
                  Закрыть
                </button>
              </div>
            )}

            {/* Role Selection */}
            {action === 'role' && (
              <div>
                <h4>Выберите роль:</h4>
                {['superadmin', 'admin', 'manager', 'client'].map(role => (
                  <button
                    key={role}
                    className={`btn ${selectedUser.role === role ? 'btn-primary' : 'btn-secondary'}`}
                    style={{ 
                      width: '100%', 
                      marginBottom: 8,
                      background: selectedUser.role === role ? ROLE_COLORS[role] : undefined
                    }}
                    onClick={() => setRole(selectedUser.id, role)}
                    disabled={saving}
                  >
                    {ROLE_LABELS[role]}
                  </button>
                ))}
                <button 
                  className="btn btn-secondary" 
                  style={{ width: '100%', marginTop: 8 }}
                  onClick={() => setAction(null)}
                >
                  ← Назад
                </button>
              </div>
            )}

            {/* Manager Selection */}
            {action === 'manager' && (
              <div>
                <h4>Выберите менеджера:</h4>
                <button
                  className="btn btn-secondary"
                  style={{ width: '100%', marginBottom: 8 }}
                  onClick={() => assignManager(selectedUser.id, null)}
                  disabled={saving}
                >
                  ❌ Без менеджера
                </button>
                {managers
                  .filter(m => m.id !== selectedUser.id)
                  .map(m => (
                    <button
                      key={m.id}
                      className={`btn ${selectedUser.manager_id === m.id ? 'btn-primary' : 'btn-secondary'}`}
                      style={{ width: '100%', marginBottom: 8 }}
                      onClick={() => assignManager(selectedUser.id, m.id)}
                      disabled={saving}
                    >
                      {ROLE_LABELS[m.role]} {m.first_name} {m.last_name}
                    </button>
                  ))}
                <button 
                  className="btn btn-secondary" 
                  style={{ width: '100%', marginTop: 8 }}
                  onClick={() => setAction(null)}
                >
                  ← Назад
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
